import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ShieldAlert, Check, X, Play, RefreshCw, Monitor, Settings, Plus, Trash2, ShieldOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(ts); }
}

const STATUS = {
  suggested: { label: 'Sugerida', color: 'var(--accent)', bg: 'rgba(27,127,249,0.13)' },
  running:   { label: 'Ejecutando', color: '#ef9f27', bg: 'rgba(239,159,39,0.15)' },
  resolved:  { label: 'Resuelta', color: 'var(--green)', bg: 'rgba(34,197,94,0.14)' },
  failed:    { label: 'Falló', color: '#e24b4a', bg: 'rgba(226,75,74,0.15)' },
  dismissed: { label: 'Descartada', color: 'var(--ink-3)', bg: 'rgba(148,163,184,0.15)' },
  approved:  { label: 'Aprobada', color: 'var(--accent)', bg: 'rgba(27,127,249,0.13)' },
};

export default function Remediation() {
  const [jobs, setJobs] = useState([]);
  const [rules, setRules] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [addCat, setAddCat] = useState(null);       // categoría en la que se está agregando
  const [candidates, setCandidates] = useState([]);
  const [pick, setPick] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isAdmin = user.role === 'admin';

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/remediation/jobs`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/remediation/rules`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/remediation/allowlist`, { headers }).then(r => r.json()),
    ]).then(([j, r, a]) => {
      setJobs(Array.isArray(j) ? j : []);
      setRules(Array.isArray(r) ? r : []);
      setAllowlist(Array.isArray(a) ? a : []);
    }).catch(() => setErr('Error cargando remediaciones.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  // La remediación ya no es solo desinstalar: una exclusión huérfana del antivirus se
  // quita, no se desinstala. La etiqueta sale del campo `action` del job/regla.
  const ACCION = {
    uninstall:        { verbo: 'Desinstalar', etiqueta: 'Desinstalar' },
    remove_exclusion: { verbo: 'Quitar exclusión', etiqueta: 'Quitar exclusión del antivirus' },
  };
  const accionDe = (a) => ACCION[a] || { verbo: 'Remediar', etiqueta: a || 'Remediar' };

  const act = async (id, action) => {
    setBusy(id); setErr('');
    try {
      const r = await fetch(`${API_URL}/remediation/jobs/${id}/${action}`, { method: 'POST', headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const updateRule = async (id, patch) => {
    try {
      await fetch(`${API_URL}/remediation/rules/${id}`, { method: 'PUT', headers, body: JSON.stringify(patch) });
      load();
    } catch { setErr('Error actualizando la regla.'); }
  };

  const openAdd = async (category) => {
    setAddCat(category); setPick(''); setCandidates([]); setErr('');
    try {
      const r = await fetch(`${API_URL}/remediation/allowlist/candidates?category=${category}`, { headers });
      const d = await r.json();
      setCandidates(Array.isArray(d) ? d : []);
    } catch { setErr('Error obteniendo candidatos.'); }
  };

  const addSoftware = async () => {
    if (!pick) return;
    setBusy('add'); setErr('');
    try {
      const r = await fetch(`${API_URL}/remediation/allowlist`, {
        method: 'POST', headers, body: JSON.stringify({ category: addCat, name: pick }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setAddCat(null); setPick(''); load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const removeSoftware = async (id) => {
    try {
      await fetch(`${API_URL}/remediation/allowlist/${id}`, { method: 'DELETE', headers });
      load();
    } catch { setErr('Error quitando software.'); }
  };

  const CAT_META = {
    remote_access: { label: 'Acceso remoto', icon: Monitor },
    p2p:           { label: 'P2P / Torrent', icon: ShieldOff },
    critical_risk: { label: 'Riesgo crítico', icon: ShieldAlert },
  };

  const badge = (st) => {
    const s = STATUS[st] || { label: st, color: 'var(--ink-2)', bg: 'var(--bg-subtle)' };
    return <span style={{ fontSize: 11, fontWeight: 600, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>;
  };

  const pending = jobs.filter(j => j.status === 'suggested');
  const history = jobs.filter(j => j.status !== 'suggested');

  return (
    <div className="page">
      <div style={{ marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bot size={20} /> Remediación automática (agente virtual)
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-2)' }}>
          El agente detecta anomalías (acceso remoto / P2P no autorizado), sugiere la desinstalación en el equipo afectado y —al aprobarla— la ejecuta y resuelve el ticket.
        </p>
      </div>

      {err && <div style={{ margin: '12px 0', padding: '10px 12px', background: 'rgba(226,75,74,0.1)', color: '#e24b4a', borderRadius: 8, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '14px 0' }}>
        <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: 'var(--ink)' }}>
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Sugerencias pendientes */}
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '4px 2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldAlert size={16} style={{ color: 'var(--accent)' }} /> Pendientes de aprobación <span style={{ color: 'var(--ink-2)', fontWeight: 400 }}>({pending.length})</span>
      </h2>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>Cargando…</div>
      ) : pending.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)', fontSize: 13, marginBottom: 20 }}>
          No hay remediaciones sugeridas. El agente revisa las anomalías cada 15 minutos.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
          {pending.map(j => (
            <div key={j.id} className="card" style={{ padding: 16, borderLeft: '3px solid #1B7FF9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 240, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>
                    {accionDe(j.action).verbo}{' '}
                    <span style={{ color: '#e24b4a', wordBreak: 'break-all' }}>{j.software_name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
                    <Monitor size={13} /> {j.hostname || 'equipo'} · {j.os_name || ''}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink)', background: 'var(--bg-subtle)', borderRadius: 8, padding: '8px 12px', lineHeight: 1.5 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Bot size={12} /> Análisis IA</span>
                    <div style={{ marginTop: 3 }}>{j.ai_summary}</div>
                  </div>
                  {j.incident_id && (
                    <Link to="/incidents" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', marginTop: 6, display: 'inline-block' }}>
                      Ver incidente vinculado →
                    </Link>
                  )}
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                    <button onClick={() => act(j.id, 'approve')} disabled={busy === j.id}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#e24b4a', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: busy === j.id ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                      <Play size={14} /> Aprobar y ejecutar
                    </button>
                    <button onClick={() => act(j.id, 'dismiss')} disabled={busy === j.id}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--bg-subtle)', color: 'var(--ink-2)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <X size={14} /> Descartar
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Historial */}
      {history.length > 0 && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: '4px 2px 10px' }}>Historial</h2>
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>
                  {['Software', 'Equipo', 'Estado', 'Fecha'].map(h =>
                    <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--border)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {history.map(j => (
                    <tr key={j.id}>
                      <td style={{ padding: '9px 14px', fontWeight: 500, color: 'var(--ink)', borderBottom: '1px solid var(--border)' }}>{j.software_name}</td>
                      <td style={{ padding: '9px 14px', color: 'var(--ink-2)', borderBottom: '1px solid var(--border)' }}>{j.hostname || '—'}</td>
                      <td style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)' }}>{badge(j.status)}</td>
                      <td style={{ padding: '9px 14px', color: 'var(--ink-2)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{fmt(j.decided_at || j.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Software vigilado (allowlist por categoría) */}
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '4px 2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldAlert size={16} /> Software vigilado
      </h2>
      <p style={{ fontSize: 12, color: 'var(--ink-2)', margin: '0 2px 12px' }}>
        Programas que disparan una remediación según su categoría. Solo se puede agregar software que el motor de higiene ya clasificó como ese tipo.
      </p>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', marginBottom: 24 }}>
        {['remote_access', 'p2p', 'critical_risk'].map(cat => {
          const meta = CAT_META[cat]; const Icon = meta.icon;
          const items = allowlist.filter(a => a.category === cat);
          return (
            <div key={cat} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                <Icon size={15} style={{ color: 'var(--accent)' }} /> {meta.label}
                <span style={{ color: 'var(--ink-2)', fontWeight: 400 }}>({items.length})</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {items.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Sin software.</span>}
                {items.map(it => (
                  <span key={it.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, background: 'var(--bg-subtle)', border: '1px solid var(--border-2)', borderRadius: 6, padding: '3px 6px 3px 9px', color: 'var(--ink)' }}>
                    {it.label || it.pattern}
                    {isAdmin && <button onClick={() => removeSoftware(it.id)} title="Quitar"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', display: 'flex', padding: 0 }}><X size={13} /></button>}
                  </span>
                ))}
              </div>
              {isAdmin && (addCat === cat ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select value={pick} onChange={e => setPick(e.target.value)}
                    style={{ flex: 1, minWidth: 150, padding: '7px 9px', border: '1px solid var(--border-2)', borderRadius: 7, fontSize: 12, background: 'var(--bg-subtle)', color: 'var(--ink)' }}>
                    <option value="">{candidates.length ? 'Elegí un programa…' : 'Sin candidatos de esta categoría'}</option>
                    {candidates.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <button onClick={addSoftware} disabled={!pick || busy === 'add'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', background: 'var(--accent)', color: 'var(--surface)', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!pick || busy === 'add') ? 0.6 : 1 }}>
                    <Check size={13} /> Agregar
                  </button>
                  <button onClick={() => setAddCat(null)} style={{ padding: '7px 10px', background: 'none', border: '1px solid var(--border-2)', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: 'var(--ink-2)' }}>Cancelar</button>
                </div>
              ) : (
                <button onClick={() => openAdd(cat)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'var(--bg-subtle)', color: 'var(--ink)', border: '1px solid var(--border-2)', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Plus size={13} /> Agregar software
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Reglas */}
      <h2 style={{ fontSize: 15, fontWeight: 600, margin: '4px 2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Settings size={16} /> Reglas de remediación
      </h2>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['Regla', 'Disparador', 'Acción', 'Modo', 'Activa'].map(h =>
                <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--border)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id}>
                  <td style={{ padding: '9px 14px', fontWeight: 500, color: 'var(--ink)', borderBottom: '1px solid var(--border)' }}>{r.name}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--ink-2)', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', fontSize: 12 }}>{r.anomaly_type}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--ink-2)', borderBottom: '1px solid var(--border)' }}>{accionDe(r.action).etiqueta}</td>
                  <td style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)' }}>
                    <select value={r.mode} disabled={!isAdmin} onChange={e => updateRule(r.id, { mode: e.target.value })}
                      style={{ padding: '5px 8px', border: '1px solid var(--border-2)', borderRadius: 6, fontSize: 12, background: 'var(--bg-subtle)', color: 'var(--ink)' }}>
                      <option value="approval">Requiere aprobación</option>
                      <option value="auto">Automático</option>
                    </select>
                  </td>
                  <td style={{ padding: '9px 14px', borderBottom: '1px solid var(--border)' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: isAdmin ? 'pointer' : 'default' }}>
                      <input type="checkbox" checked={!!r.enabled} disabled={!isAdmin} onChange={e => updateRule(r.id, { enabled: e.target.checked })} />
                    </label>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--ink-2)' }}>Las reglas por defecto se crean en el primer ciclo del agente.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 10 }}>
        En modo <b>Automático</b> el agente ejecuta la remediación sin aprobación. Empezá en <b>Requiere aprobación</b> hasta tener confianza. Solo se remedian equipos Windows con software de la allowlist.
      </p>
    </div>
  );
}
